import type { ProductSlugs } from '@deque/billing-utils';
import { Toast } from '@deque/cauldron-react';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import type { IntegrationTemplate } from '~/common/api-client';
import PageTitle from '~/common/components/PageTitle';
import { type SupportedIntegrationProductSlugs } from '~/common/constants';
import { useAxeAccountAnalytics } from '~/common/contexts/analytics';
import NotFound from '~/common/pages/NotFound';
import type {
  IssueType,
  ProjectsAndIssueTypes
} from '~/common/utils/integrations-client/jira/getProjectsAndIssueTypes';
import getTemplateSteps from '../../../utils/get-template-steps';
import TemplateStepper from './TemplateStepper/TemplateStepper';
import styles from './TemplateWizard.css';
import { IssueTypeFieldsProvider } from './TemplateWizardForm/IssueTypeFieldsContext';
import TemplateWizardForm, {
  defaultTemplateValues,
  getTemplateSchema
} from './TemplateWizardForm/TemplateWizardForm';

type QueryParamsType = {
  id: string;
};

interface TemplateWizardProps {
  token: string;
  instanceUrl: string;
  isEditMode?: boolean;
  enterpriseId: string;
  connectionId: string;
  refreshTemplates: () => void;
  templates: IntegrationTemplate[];
  projects: ProjectsAndIssueTypes[];
  getIssueTypeById: (id: string) => IssueType | undefined;
  integrationProductSlug:
    | SupportedIntegrationProductSlugs
    | ProductSlugs.axeAuditorIntegration;
  getProjectById: (id: string) => ProjectsAndIssueTypes | undefined;
  getIssueTypesByProjectId: (id: string | null) => IssueType[] | undefined;
  userKeycloakId?: string;
  isUserAdmin?: boolean;
}

const TemplateWizard = ({
  token,
  projects,
  templates,
  instanceUrl,
  connectionId,
  enterpriseId,
  getProjectById,
  getIssueTypeById,
  refreshTemplates,
  isEditMode = false,
  integrationProductSlug,
  getIssueTypesByProjectId,
  userKeycloakId = '',
  isUserAdmin = false
}: TemplateWizardProps) => {
  const { t } = useTranslation();
  const axeAccountAnalytics = useAxeAccountAnalytics();
  const { id } = useParams<QueryParamsType>();
  const [currentStep, setCurrentStep] = useState(0);
  const [isStepDataLoading, setIsStepDataLoading] = useState(false);
  const [isStepDataError, setIsStepDataError] = useState(false);

  useEffect(() => {
    axeAccountAnalytics.viewIntegrationsTemplatePage();
  }, []);

  const steps = useMemo(
    () =>
      getTemplateSteps({
        t,
        isPriorityEnabled: true,
        integrationProductSlug,
        instanceUrl,
        setIsStepDataLoading,
        setCurrentStep,
        projects,
        getProjectById,
        getIssueTypeById,
        getIssueTypesByProjectId,
        connectionId,
        token,
        setIsStepDataError
      }),
    [isEditMode, integrationProductSlug]
  );

  const templateSchema = useMemo(() => getTemplateSchema(t), [t]);
  const methods = useForm({
    resolver: zodResolver(templateSchema),
    defaultValues: defaultTemplateValues
  });

  useEffect(() => {
    if (isEditMode) {
      setCurrentStep(steps.length - 1);
    }
  }, [isEditMode, steps]);

  const initialTemplate = templates.find(template => template.id === id);
  const canManageTemplate = isEditMode
    ? isUserAdmin || initialTemplate?.created_by === userKeycloakId
    : true;
  const currentProject = projects.find(
    project => project.id === initialTemplate?.project_id
  );
  const currentIssueType = currentProject?.issueTypes?.find(
    issueType => issueType.id === initialTemplate?.issue_type_id
  );

  const isProjectOrIssueTypeNotFound =
    initialTemplate && (!currentProject || !currentIssueType);

  if (isEditMode && (!initialTemplate || !canManageTemplate)) {
    return <NotFound />;
  }

  if (isProjectOrIssueTypeNotFound) {
    return (
      <>
        <PageTitle title={t('Project or issue type not found')} />
        <div className={styles.notFound}>
          <h1>{t('Cannot edit template. Project or issue type not found.')}</h1>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle
        title={t('{{action}} Template - Step {{step}}: {{stepTitle}}', {
          action: isEditMode ? t('Edit') : t('Add'),
          step: currentStep + 1,
          stepTitle: steps[currentStep].header
        })}
      />
      {!projects.length && (
        <Toast type="error" show>
          {t(
            'No projects with issue types and permission to create issues found'
          )}
        </Toast>
      )}
      <TemplateStepper currentStep={currentStep} steps={steps} />
      <FormProvider {...methods}>
        <IssueTypeFieldsProvider
          enterpriseId={enterpriseId}
          connectionId={connectionId}
          token={token}
          initialTemplate={initialTemplate}
        >
          <TemplateWizardForm
            integrationProductSlug={integrationProductSlug}
            initialTemplate={initialTemplate}
            connectionId={connectionId}
            token={token}
            enterpriseId={enterpriseId}
            templates={templates}
            refreshTemplates={refreshTemplates}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            steps={steps}
            isStepDataLoading={isStepDataLoading}
            projects={projects}
            isStepDataError={isStepDataError}
          />
        </IssueTypeFieldsProvider>
      </FormProvider>
    </>
  );
};

export default TemplateWizard;
